import React, { useEffect } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { PageBreadcrumbs } from "../../../components/common/PageBreadcrumbs";
import { NavigationBlocker } from "../../../components/common/NavigationBlocker";
import { useShopifySaveBar } from "../../../hooks/useShopifySaveBar";
import {
  useQuantityTiersEditorStore,
  selectQuantityTiersIsDirty,
} from "../../../store/useQuantityTiersEditorStore";
import { useOffersListStore } from "../../../store/useOffersListStore";
import { SalesBoosterTabBar } from "../SalesBoosterTabBar";
import {
  NameAndProductSection,
  TiersSection,
} from "./QuantityTiersEditorConfig";
import { DesignSection } from "./QuantityTiersEditorDesign";
import { QuantityTiersEditorPreview } from "./QuantityTiersEditorPreview";

// ─── Page ─────────────────────────────────────────────────────────────────────

const QuantityTiersEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  const {
    config,
    offerId,
    isLoading,
    isSaving,
    updateField,
    addTier,
    removeTier,
    updateTier,
    initNew,
    loadFromBackend,
    save,
    discardChanges,
    reset,
  } = useQuantityTiersEditorStore(
    useShallow((s) => ({
      config: s.config,
      offerId: s.offerId,
      isLoading: s.isLoading,
      isSaving: s.isSaving,
      updateField: s.updateField,
      addTier: s.addTier,
      removeTier: s.removeTier,
      updateTier: s.updateTier,
      initNew: s.initNew,
      loadFromBackend: s.loadFromBackend,
      save: s.save,
      discardChanges: s.discardChanges,
      reset: s.reset,
    }))
  );
  const {
    internalName,
    isActive,
    targetProduct,
    tiers,
    layout,
    backgroundColor,
    selectedBackgroundColor,
    activeBorderColor,
    borderRadius,
    hideProductImage,
    hideVariantQuantitySelectors,
    disableVariantSelectors,
    useCompareAtPriceAsBase,
    disableIfExceedsMaxQuantity,
  } = config;
  const isDirty = useQuantityTiersEditorStore(selectQuantityTiersIsDirty);
  const refetchOffers = useOffersListStore((s) => s.refetch);

  useEffect(() => {
    if (routeId) {
      loadFromBackend(routeId).catch(() => {
        window.shopify?.toast?.show("No se pudo cargar la oferta.", {
          isError: true,
        });
      });
    } else if (offerId === null) {
      initNew();
    }
  }, [routeId, offerId, initNew, loadFromBackend]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useShopifySaveBar({
    isDirty,
    isSaving,
    onSave: async () => {
      try {
        const savedId = await save();
        // Bust the offers-list cache so the new/updated offer is reflected
        // when the merchant navigates back (mirrors the upsell editors).
        refetchOffers().catch(() => {});
        if (!routeId) {
          navigate(`/sales-booster/quantity-offers/${savedId}`, {
            replace: true,
          });
        }
        window.shopify?.toast?.show("Oferta guardada correctamente.");
      } catch {
        window.shopify?.toast?.show("No se pudo guardar la oferta.", {
          isError: true,
        });
      }
    },
    onDiscard: discardChanges,
  });

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={routeId ? "Editar oferta" : "Nueva oferta"}>
        <PageBreadcrumbs
          items={[
            { label: "Impulsor de ventas", to: "/sales-booster" },
            {
              label: "Ofertas de Cantidad",
              to: "/sales-booster/quantity-offers",
            },
          ]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {/* ── Header (active toggle + status) ── */}
          <div className="flex items-center justify-end py-1">
            <div className="flex items-center gap-3">
              <s-switch
                label="Activar oferta"
                labelAccessibilityVisibility="exclusive"
                checked={isActive}
                onChange={(e: CallbackEvent<"s-switch">) =>
                  updateField("isActive", e.currentTarget.checked)
                }
              />
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                {isActive ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>

          {isLoading && (
            <s-banner tone="info">
              Cargando configuración de la oferta…
            </s-banner>
          )}

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: sections */}
            <s-stack direction="block" gap="base">
              <NameAndProductSection
                name={internalName}
                targetProduct={targetProduct}
                onNameChange={(v) => updateField("internalName", v)}
                onProductChange={(p) => updateField("targetProduct", p)}
                onProductRemove={() => updateField("targetProduct", null)}
              />

              {targetProduct && (
                <>
                  <TiersSection
                    tiers={tiers}
                    onAdd={addTier}
                    onRemove={removeTier}
                    onUpdate={updateTier}
                  />

                  <DesignSection
                    layout={layout}
                    backgroundColor={backgroundColor}
                    selectedBackgroundColor={selectedBackgroundColor}
                    activeBorderColor={activeBorderColor}
                    borderRadius={borderRadius}
                    hideProductImage={hideProductImage}
                    hideVariantQuantitySelectors={hideVariantQuantitySelectors}
                    disableVariantSelectors={disableVariantSelectors}
                    useCompareAtPriceAsBase={useCompareAtPriceAsBase}
                    disableIfExceedsMaxQuantity={disableIfExceedsMaxQuantity}
                    onLayoutChange={(v) => updateField("layout", v)}
                    onBackgroundColorChange={(v) =>
                      updateField("backgroundColor", v)
                    }
                    onSelectedBackgroundColorChange={(v) =>
                      updateField("selectedBackgroundColor", v)
                    }
                    onActiveBorderColorChange={(v) =>
                      updateField("activeBorderColor", v)
                    }
                    onBorderRadiusChange={(v) => updateField("borderRadius", v)}
                    onHideProductImageChange={(v) =>
                      updateField("hideProductImage", v)
                    }
                    onHideVariantQuantitySelectorsChange={(v) =>
                      updateField("hideVariantQuantitySelectors", v)
                    }
                    onDisableVariantSelectorsChange={(v) =>
                      updateField("disableVariantSelectors", v)
                    }
                    onUseCompareAtPriceAsBaseChange={(v) =>
                      updateField("useCompareAtPriceAsBase", v)
                    }
                    onDisableIfExceedsMaxQuantityChange={(v) =>
                      updateField("disableIfExceedsMaxQuantity", v)
                    }
                  />
                </>
              )}
            </s-stack>

            {/* Right: preview */}
            <QuantityTiersEditorPreview state={config} />
          </div>
        </s-stack>
      </s-page>
    </div>
  );
};

export default QuantityTiersEditor;
